/**
 * REUSABLE UTILITY MODULE - Validators
 * Can be used in any React project
 */

/**
 * Validate email address
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate French phone number
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid
 */
export const isValidPhone = (phone) => {
  if (!phone) return false;
  const cleaned = phone.replace(/\D/g, '');
  // French phone: 10 digits starting with 0, or 11 digits starting with 33
  return (cleaned.length === 10 && cleaned.startsWith('0')) ||
         (cleaned.length === 11 && cleaned.startsWith('33'));
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @param {object} options - Validation options
 * @returns {object} { isValid: boolean, errors: string[] }
 */
export const validatePassword = (password, options = {}) => {
  const {
    minLength = 8,
    requireUppercase = true,
    requireLowercase = true,
    requireNumbers = true,
    requireSpecialChars = false,
  } = options;

  const errors = [];

  if (!password) {
    errors.push('Le mot de passe est requis');
    return { isValid: false, errors };
  }

  if (password.length < minLength) {
    errors.push(`Le mot de passe doit contenir au moins ${minLength} caractères`);
  }

  if (requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Le mot de passe doit contenir au moins une majuscule');
  }

  if (requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Le mot de passe doit contenir au moins une minuscule');
  }

  if (requireNumbers && !/\d/.test(password)) {
    errors.push('Le mot de passe doit contenir au moins un chiffre');
  }

  if (requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Le mot de passe doit contenir au moins un caractère spécial');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate URL
 * @param {string} url - URL to validate
 * @returns {boolean} True if valid
 */
export const isValidUrl = (url) => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Validate credit card number (Luhn algorithm)
 * @param {string} cardNumber - Card number to validate
 * @returns {boolean} True if valid
 */
export const isValidCreditCard = (cardNumber) => {
  if (!cardNumber) return false;

  const cleaned = cardNumber.replace(/\D/g, '');
  if (cleaned.length < 13 || cleaned.length > 19) return false;

  let sum = 0;
  let isEven = false;

  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

/**
 * Validate date format (YYYY-MM-DD)
 * @param {string} date - Date to validate
 * @returns {boolean} True if valid
 */
export const isValidDate = (date) => {
  if (!date) return false;
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(date)) return false;

  const parsedDate = new Date(date);
  return !isNaN(parsedDate.getTime());
};

/**
 * Validate date is in the past
 * @param {string|Date} date - Date to validate
 * @returns {boolean} True if in the past
 */
export const isDateInPast = (date) => {
  if (!date) return false;
  const parsedDate = new Date(date);
  return parsedDate < new Date();
};

/**
 * Validate date is in the future
 * @param {string|Date} date - Date to validate
 * @returns {boolean} True if in the future
 */
export const isDateInFuture = (date) => {
  if (!date) return false;
  const parsedDate = new Date(date);
  return parsedDate > new Date();
};

/**
 * Validate age range
 * @param {string|Date} birthdate - Date of birth
 * @param {number} minAge - Minimum age
 * @param {number} maxAge - Maximum age
 * @returns {boolean} True if age is in range
 */
export const isAgeInRange = (birthdate, minAge, maxAge) => {
  if (!birthdate) return false;

  const today = new Date();
  const birth = new Date(birthdate);
  const age = Math.floor((today - birth) / (365.25 * 24 * 60 * 60 * 1000));

  return age >= minAge && age <= maxAge;
};

/**
 * Validate required field
 * @param {any} value - Value to validate
 * @returns {boolean} True if not empty
 */
export const isRequired = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

/**
 * Validate minimum length
 * @param {string} value - Value to validate
 * @param {number} minLength - Minimum length
 * @returns {boolean} True if meets minimum
 */
export const hasMinLength = (value, minLength) => {
  if (!value) return false;
  return value.length >= minLength;
};

/**
 * Validate maximum length
 * @param {string} value - Value to validate
 * @param {number} maxLength - Maximum length
 * @returns {boolean} True if under maximum
 */
export const hasMaxLength = (value, maxLength) => {
  if (!value) return true;
  return value.length <= maxLength;
};

/**
 * Validate number range
 * @param {number} value - Value to validate
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {boolean} True if in range
 */
export const isInRange = (value, min, max) => {
  if (value === null || value === undefined) return false;
  return value >= min && value <= max;
};

/**
 * Validate if value is numeric
 * @param {any} value - Value to validate
 * @returns {boolean} True if numeric
 */
export const isNumeric = (value) => {
  if (value === null || value === undefined) return false;
  return !isNaN(parseFloat(value)) && isFinite(value);
};

/**
 * Validate if value is integer
 * @param {any} value - Value to validate
 * @returns {boolean} True if integer
 */
export const isInteger = (value) => {
  if (value === null || value === undefined) return false;
  return Number.isInteger(parseFloat(value));
};

/**
 * Validate if value is positive number
 * @param {number} value - Value to validate
 * @returns {boolean} True if positive
 */
export const isPositive = (value) => {
  if (value === null || value === undefined) return false;
  return parseFloat(value) > 0;
};

/**
 * Validate alphanumeric
 * @param {string} value - Value to validate
 * @returns {boolean} True if alphanumeric
 */
export const isAlphanumeric = (value) => {
  if (!value) return false;
  return /^[a-zA-Z0-9]+$/.test(value);
};

/**
 * Validate alphabetic only
 * @param {string} value - Value to validate
 * @returns {boolean} True if alphabetic
 */
export const isAlphabetic = (value) => {
  if (!value) return false;
  return /^[a-zA-ZÀ-ÿ\s]+$/.test(value);
};

/**
 * Validate file type
 * @param {File} file - File to validate
 * @param {string[]} allowedTypes - Allowed MIME types
 * @returns {boolean} True if type is allowed
 */
export const isValidFileType = (file, allowedTypes) => {
  if (!file || !allowedTypes) return false;
  return allowedTypes.includes(file.type);
};

/**
 * Validate file size
 * @param {File} file - File to validate
 * @param {number} maxSizeInMB - Maximum size in MB
 * @returns {boolean} True if under max size
 */
export const isValidFileSize = (file, maxSizeInMB) => {
  if (!file) return false;
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  return file.size <= maxSizeInBytes;
};

/**
 * Validate postal code (French)
 * @param {string} postalCode - Postal code to validate
 * @returns {boolean} True if valid
 */
export const isValidPostalCode = (postalCode) => {
  if (!postalCode) return false;
  return /^[0-9]{5}$/.test(postalCode);
};

/**
 * Sanitize string (remove HTML tags and dangerous characters)
 * @param {string} str - String to sanitize
 * @returns {string} Sanitized string
 */
export const sanitizeString = (str) => {
  if (!str) return '';
  return str
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/[<>'"&]/g, '') // Remove dangerous characters
    .trim();
};

/**
 * Validate form data against schema
 * @param {object} data - Form data
 * @param {object} schema - Validation schema
 * @returns {object} { isValid: boolean, errors: object }
 */
export const validateForm = (data, schema) => {
  const errors = {};
  let isValid = true;

  Object.keys(schema).forEach((field) => {
    const rules = schema[field];
    const value = data[field];

    rules.forEach((rule) => {
      if (rule.type === 'required' && !isRequired(value)) {
        errors[field] = rule.message || 'Ce champ est requis';
        isValid = false;
      } else if (rule.type === 'email' && value && !isValidEmail(value)) {
        errors[field] = rule.message || 'Email invalide';
        isValid = false;
      } else if (rule.type === 'minLength' && value && !hasMinLength(value, rule.value)) {
        errors[field] = rule.message || `Minimum ${rule.value} caractères requis`;
        isValid = false;
      } else if (rule.type === 'maxLength' && value && !hasMaxLength(value, rule.value)) {
        errors[field] = rule.message || `Maximum ${rule.value} caractères autorisés`;
        isValid = false;
      } else if (rule.type === 'pattern' && value && !rule.pattern.test(value)) {
        errors[field] = rule.message || 'Format invalide';
        isValid = false;
      } else if (rule.type === 'custom' && value && !rule.validator(value)) {
        errors[field] = rule.message || 'Validation échouée';
        isValid = false;
      }
    });
  });

  return { isValid, errors };
};

const validatorsExport = {
  isValidEmail,
  isValidPhone,
  validatePassword,
  isValidUrl,
  isValidCreditCard,
  isValidDate,
  isDateInPast,
  isDateInFuture,
  isAgeInRange,
  isRequired,
  hasMinLength,
  hasMaxLength,
  isInRange,
  isNumeric,
  isInteger,
  isPositive,
  isAlphanumeric,
  isAlphabetic,
  isValidFileType,
  isValidFileSize,
  isValidPostalCode,
  sanitizeString,
  validateForm,
};

export default validatorsExport;
